import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Bell, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, parseErrorMessage, queryClient } from "@/lib/queryClient";

interface AdminAgentRow {
  id: number;
  accountId: number;
  name: string;
  city: string;
  state: string;
  active: boolean;
  lastRunAt: string | null;
  lastRunMatches: number | null;
  lastRunError: string | null;
}
interface BuyBoxAutoAdminResponse {
  globalEnabled: boolean;
  thresholdCents: number;
  overThresholdToday: boolean;
  costByDay: Array<{ date: string; costCents: number; runs: number }>;
  accounts: Array<{ accountId: number; email: string | null; disabled: boolean; disabledReason: string | null; agents: AdminAgentRow[] }>;
}

const QUERY_KEY = ["/api/admin/buy-box-auto"] as const;
const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/** Admin → Billing: daily buy box agents (B21) — kill switches, costs, threshold, Run now. */
export function BuyBoxAutoAdminCard() {
  const { toast } = useToast();
  const q = useQuery<BuyBoxAutoAdminResponse>({ queryKey: QUERY_KEY });
  const [threshold, setThreshold] = useState("");
  useEffect(() => {
    if (q.data) setThreshold(String(q.data.thresholdCents / 100));
  }, [q.data?.thresholdCents]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  const fail = (err: unknown) => toast({ title: "Couldn't update", description: parseErrorMessage(err, "Please try again."), variant: "destructive" });

  const settings = useMutation({
    mutationFn: (body: { globalEnabled?: boolean; thresholdCents?: number }) => apiRequest("POST", "/api/admin/buy-box-auto/settings", body),
    onSuccess: () => {
      toast({ title: "Saved" });
      void refresh();
    },
    onError: fail,
  });
  const accountSwitch = useMutation({
    mutationFn: ({ accountId, disabled }: { accountId: number; disabled: boolean }) =>
      apiRequest("POST", `/api/admin/buy-box-auto/accounts/${accountId}`, { disabled }),
    onSuccess: () => void refresh(),
    onError: fail,
  });
  const runNow = useMutation({
    mutationFn: async (accountId: number) => (await apiRequest("POST", "/api/admin/buy-box-auto/run-now", { accountId })).json() as Promise<{ started: number }>,
    onSuccess: (r) => toast({ title: `Running ${r.started} agent(s)`, description: "The email goes out when the scans finish, only if there are new matches." }),
    onError: fail,
  });

  const data = q.data;
  const today = data?.costByDay[data.costByDay.length - 1];
  return (
    <Card className="p-5 space-y-4" data-testid="card-admin-buy-box-auto">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-blue-600" />
          <h3 className="font-semibold">Daily buy box agents</h3>
        </div>
        {data && (
          <div className="flex items-center gap-2 text-sm">
            <span>{data.globalEnabled ? "On for everyone" : "Off for everyone"}</span>
            <Switch
              checked={data.globalEnabled}
              disabled={settings.isPending}
              onCheckedChange={(v) => settings.mutate({ globalEnabled: v })}
              aria-label="Global daily buy box agents switch"
              data-testid="switch-buy-box-auto-global"
            />
          </div>
        )}
      </div>
      {q.isLoading || !data ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          {data.overThresholdToday && today && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="alert-buy-box-auto-cost">
              Today's estimated cost is {usd(today.costCents)}, over the {usd(data.thresholdCents)} alert threshold.
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="buy-box-auto-threshold" className="text-xs">Daily cost alert ($)</Label>
              <Input id="buy-box-auto-threshold" className="h-9 w-28" inputMode="decimal" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="input-buy-box-auto-threshold" />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={settings.isPending || !Number.isFinite(Number(threshold)) || Number(threshold) < 0}
              onClick={() => settings.mutate({ thresholdCents: Math.round(Number(threshold) * 100) })}
              data-testid="button-save-buy-box-auto-threshold"
            >
              Save threshold
            </Button>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Estimated cost, last 7 days (UTC)</p>
            <div className="grid grid-cols-7 gap-1 text-center text-xs" data-testid="grid-buy-box-auto-costs">
              {data.costByDay.map((d) => (
                <div key={d.date} className={`rounded border p-1 ${d.costCents > data.thresholdCents ? "border-red-300 bg-red-50" : ""}`}>
                  <div className="text-muted-foreground">{d.date.slice(5)}</div>
                  <div className="font-medium">{usd(d.costCents)}</div>
                  <div className="text-muted-foreground">{d.runs} runs</div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {data.accounts.length === 0 && <p className="text-sm text-muted-foreground">No accounts have agents yet.</p>}
            {data.accounts.map((acct) => (
              <div key={acct.accountId} className="rounded-md border p-3" data-testid={`row-buy-box-auto-account-${acct.accountId}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium">
                    {acct.email ?? `Account ${acct.accountId}`} <span className="text-muted-foreground">#{acct.accountId}</span>
                    {acct.disabled && <Badge variant="destructive" className="ml-2">Killed</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={runNow.isPending || acct.disabled || !data.globalEnabled} onClick={() => runNow.mutate(acct.accountId)} data-testid={`button-run-now-${acct.accountId}`}>
                      {runNow.isPending && runNow.variables === acct.accountId ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run now"}
                    </Button>
                    <Button size="sm" variant={acct.disabled ? "default" : "destructive"} disabled={accountSwitch.isPending} onClick={() => accountSwitch.mutate({ accountId: acct.accountId, disabled: !acct.disabled })} data-testid={`button-kill-${acct.accountId}`}>
                      {acct.disabled ? "Turn back on" : "Kill for account"}
                    </Button>
                  </div>
                </div>
                <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                  {acct.agents.map((a) => (
                    <li key={a.id}>
                      {a.name} · {a.city}, {a.state} · {a.active ? "on" : "paused"}
                      {a.lastRunAt ? ` · last run ${new Date(a.lastRunAt).toLocaleString()} (${a.lastRunError ? `error: ${a.lastRunError}` : `${a.lastRunMatches ?? 0} new`})` : " · not run yet"}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
